import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, RefreshCw, ShieldQuestion } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const API_URL = getApiUrl();
const LABELS = {
  enrolled: 'Enrolled', consultations: 'Consultations', conversion_pct: 'Conversion %',
  first_enrolment_inr: 'First-enrolment ₹', renewal_inr: 'Renewal ₹', fx_usd: 'FX USD→INR',
};
const SRC = { ops: 'Overall', sales: 'Sales', presales: 'Pre-Sales', marketing: 'Marketing' };

// Compares the shared metrics across dashboards for the given period.
export const DataCheckChip = ({ startDate, endDate, className = '' }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await axios.get(`${API_URL}/api/analytics/reconcile`, { params, withCredentials: true });
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Check unavailable' });
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const status = state.loading ? 'loading' : state.error ? 'error' : state.data?.status;
  const drift = state.data?.drift_count || 0;
  const style = {
    ok: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    partial: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    drift: 'border-amber-300 bg-amber-100 text-amber-900 animate-pulse',
    loading: 'border-border bg-muted text-slate-500',
    error: 'border-border bg-muted text-slate-500',
  }[status] || 'border-border bg-muted text-slate-500';
  const Icon = status === 'drift' ? AlertTriangle : status === 'ok' || status === 'partial' ? CheckCircle2 : ShieldQuestion;
  const text = status === 'loading' ? 'Checking data…' : status === 'drift' ? `Data drift (${drift})` : status === 'error' ? 'Data check n/a' : 'Data verified';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="data-check-chip"
          data-status={status}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${style} ${className}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {text}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 bg-popover" data-testid="data-check-popover">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <p className="font-display text-sm font-semibold">Cross-dashboard check</p>
          <button type="button" onClick={load} className="btn-ghost p-1" aria-label="Re-run check" data-testid="data-check-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${state.loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {state.error && <p className="px-4 py-3 text-sm text-slate-600">{String(state.error)}</p>}
        {state.data && (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-xs">
              <thead className="table-header">
                <tr><th className="px-3 py-2 text-left">Metric</th><th className="px-3 py-2 text-left">Sources</th><th className="px-3 py-2 text-right">Δ</th></tr>
              </thead>
              <tbody>
                {state.data.metrics.map((m) => (
                  <tr key={m.metric} className={`border-t border-border/60 ${m.ok ? '' : 'bg-amber-50'}`} data-testid={`data-check-row-${m.metric}`}>
                    <td className="px-3 py-2 font-medium text-slate-700">{LABELS[m.metric] || m.metric}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                      {Object.entries(m.values).map(([k, v]) => (
                        <div key={k}>{SRC[k] || k}: {typeof v === 'number' ? v.toLocaleString('en-IN') : v}</div>
                      ))}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${m.ok ? 'text-emerald-700' : 'text-amber-800 font-semibold'}`}>{m.ok ? '0' : m.spread.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {Object.keys(state.data.errors || {}).length > 0 && (
              <p className="border-t border-border px-4 py-2 text-[11px] text-slate-500">
                Not compared: {Object.keys(state.data.errors).map((k) => SRC[k] || k).join(', ')}
              </p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
